<template>
  <div
    ref="container"
    :style="containerStyle"
    class="draggable-container"
    @contextmenu.prevent="openContextMenu"
    @touchstart="onTouchStart"
  >
    <img
      :src="props.imageSrc"
      alt="Label Image"
      class="label-image"
      :style="{ objectFit: (fit === 'cover' ? 'cover' : 'contain') }"
      :crossorigin="props.imageSrc?.startsWith('data:') ? undefined : 'anonymous'"
      decoding="sync"
      loading="eager"
      :data-fit="fit"
    />

    <!-- Resize handles -->
    <div
      class="resize-handle top-left"
      @pointerdown.stop="initResize('top-left', $event)"
      @touchstart.stop="initTouchResize('top-left', $event)"
    />
    <div
      class="resize-handle top-right"
      @pointerdown.stop="initResize('top-right', $event)"
      @touchstart.stop="initTouchResize('top-right', $event)"
    />
    <div
      class="resize-handle bottom-left"
      @pointerdown.stop="initResize('bottom-left', $event)"
      @touchstart.stop="initTouchResize('bottom-left', $event)"
    />
    <div
      class="resize-handle bottom-right"
      @pointerdown.stop="initResize('bottom-right', $event)"
      @touchstart.stop="initTouchResize('bottom-right', $event)"
    />

    <!-- Kontextové menu -->
    <div
      v-if="contextMenu.visible"
      :style="{ top: contextMenu.y + 'px', left: contextMenu.x + 'px' }"
      class="context-menu"
    >
      <button @click="resetToOriginalSize">Obnoviť pôvodný rozmer</button>
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  ref,
  reactive,
  computed,
  onMounted,
  onBeforeUnmount,
  defineProps,
  defineExpose,
  watch,
  nextTick,
  type CSSProperties, withCtx,
} from 'vue'
import { useCanvasUnits } from '@/composables/useCanvasUnits.ts'
import { useDesignerStore } from '@/stores/designerStore.ts'

interface Props {
  id?: number | null
  imageSrc: string
  width: number
  height: number
  positionX: number
  positionY: number
  fit: 'cover' | 'contain';
}

const props = withDefaults(defineProps<Props>(), {
  fit: 'contain'
})
const emit = defineEmits(['update:width', 'update:height', 'update:positionX', 'update:positionY'])

const { mmToPx, pxToMm } = useCanvasUnits()

const container = ref<HTMLElement | null>(null)

// --- pixelový stav (odvodený z props a škály) ---
const pos = reactive({ x: 0, y: 0 })
const widthPx = computed(() => mmToPx(props.width, 'x'))
const heightPx = computed(() => mmToPx(props.height, 'y'))

// Pôvodné rozmery pre „reset“
const originalDimensions = reactive({ width: 0, height: 0 })
const originalAspectRatio = computed(() =>
  originalDimensions.height > 0 ? originalDimensions.width / originalDimensions.height : 1
)

// „live“ stav počas interakcie (drag/resize)
const live = reactive({ x: 0, y: 0, width: 0, height: 0 })

// --- Kontextové menu ---
const contextMenu = reactive({ visible: false, x: 0, y: 0 })
const openContextMenu = (e: MouseEvent) => {
  contextMenu.visible = true
  contextMenu.x = e.clientX
  contextMenu.y = e.clientY
  document.addEventListener('click', closeContextMenu)
}
const closeContextMenu = () => {
  contextMenu.visible = false
  document.removeEventListener('click', closeContextMenu)
}
const resetToOriginalSize = () => {
  emit('update:width', +pxToMm(originalDimensions.width, 'x').toFixed(2))
  emit('update:height', +pxToMm(originalDimensions.height, 'y').toFixed(2))
  contextMenu.visible = false
}

// --- Dragovanie ---
const dragOffset = reactive({ x: 0, y: 0 })
const isDragging = ref(false)
const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max)

const onContainerPointerDown = (e: PointerEvent) => {
  if ((e.target as HTMLElement).classList.contains('resize-handle')) return
    ;(e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId)
  contextMenu.visible = false

  dragOffset.x = e.clientX - pos.x
  dragOffset.y = e.clientY - pos.y
  isDragging.value = true

  window.addEventListener('pointermove', onContainerPointerMove)
  window.addEventListener('pointerup', onContainerPointerUp)
  window.addEventListener('pointercancel', onContainerPointerUp)
}

const onContainerPointerMove = (e: PointerEvent) => {
  if (!isDragging.value) return
  const parent = container.value?.parentElement
  if (!parent) return
  const rect = parent.getBoundingClientRect()
  const w = resizeState.active ? live.width : widthPx.value
  const h = resizeState.active ? live.height : heightPx.value
  const nextX = clamp(e.clientX - dragOffset.x, 0, rect.width - w)
  const nextY = clamp(e.clientY - dragOffset.y, 0, rect.height - h)
  pos.x = nextX
  pos.y = nextY
  live.x = nextX
  live.y = nextY
}

const onContainerPointerUp = () => {
  isDragging.value = false
  window.removeEventListener('pointermove', onContainerPointerMove)
  window.removeEventListener('pointerup', onContainerPointerUp)
  window.removeEventListener('pointercancel', onContainerPointerUp)
  emit('update:positionX', +pxToMm(pos.x, 'x').toFixed(2))
  emit('update:positionY', +pxToMm(pos.y, 'y').toFixed(2))
}

// --- Touch drag ---
const onTouchStart = (e: TouchEvent) => {
  if (e.touches.length !== 1) return
  if (resizeState.active) return
  const touch = e.touches[0]
  dragOffset.x = touch.clientX - pos.x
  dragOffset.y = touch.clientY - pos.y
  isDragging.value = true
  window.addEventListener('touchmove', onTouchMove, { passive: false })
  window.addEventListener('touchend', onTouchEnd)
  window.addEventListener('touchcancel', onTouchEnd)
}

const onTouchMove = (e: TouchEvent) => {
  if (!isDragging.value) return
  const touch = e.touches[0]
  const parent = container.value?.parentElement
  if (!parent) return
  const rect = parent.getBoundingClientRect()
  const w = resizeState.active ? live.width : widthPx.value
  const h = resizeState.active ? live.height : heightPx.value
  const nextX = clamp(touch.clientX - dragOffset.x, 0, rect.width - w)
  const nextY = clamp(touch.clientY - dragOffset.y, 0, rect.height - h)
  pos.x = nextX
  pos.y = nextY
  live.x = nextX
  live.y = nextY
}

const onTouchEnd = () => {
  isDragging.value = false
  window.removeEventListener('touchmove', onTouchMove)
  window.removeEventListener('touchend', onTouchEnd)
  window.removeEventListener('touchcancel', onTouchEnd)
  emit('update:positionX', +pxToMm(pos.x, 'x').toFixed(2))
  emit('update:positionY', +pxToMm(pos.y, 'y').toFixed(2))
}

// --- Resize (handles + multitouch) ---
const resizeState = reactive({
  active: false,
  handle: '' as 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right' | '',
  startX: 0,
  startY: 0,
  startWidth: 0,
  startHeight: 0,
  startPosX: 0,
  startPosY: 0,
  pointerId: 0,
})

const isLeftHandle = (h: string) => h.includes('left')
const isTopHandle = (h: string) => h.includes('top')

function clampRect(x: number, y: number, w: number, h: number) {
  const parent = container.value?.parentElement
  if (!parent) return { x, y, w, h }
  const rect = parent.getBoundingClientRect()
  w = Math.min(Math.max(w, 20), rect.width)
  h = Math.min(Math.max(h, 20), rect.height)
  x = Math.min(Math.max(x, 0), rect.width - w)
  y = Math.min(Math.max(y, 0), rect.height - h)
  return { x, y, w, h }
}

const initResize = (handle: typeof resizeState.handle, e: PointerEvent) => {
  e.stopPropagation()
  resizeState.active = true
  resizeState.handle = handle
  resizeState.startX = e.clientX
  resizeState.startY = e.clientY
  resizeState.startWidth = widthPx.value
  resizeState.startHeight = heightPx.value
  resizeState.startPosX = pos.x
  resizeState.startPosY = pos.y
  resizeState.pointerId = e.pointerId
  ;(e.currentTarget as HTMLElement)?.setPointerCapture?.(e.pointerId)
  contextMenu.visible = false

  live.width = widthPx.value
  live.height = heightPx.value
  live.x = pos.x
  live.y = pos.y

  window.addEventListener('pointermove', resizePointerMove)
  window.addEventListener('pointerup', resizePointerUp)
  window.addEventListener('pointercancel', resizePointerUp)
}

const resizePointerMove = (e: PointerEvent) => {
  // SHIFT = uzamkni pomer strán (zvyšok voľný resize)
  updateResize(e.clientX, e.clientY, e.shiftKey)
}

const resizePointerUp = () => {
  resizeState.active = false
  window.removeEventListener('pointermove', resizePointerMove)
  window.removeEventListener('pointerup', resizePointerUp)
  window.removeEventListener('pointercancel', resizePointerUp)

  emit('update:width', +pxToMm(live.width, 'x').toFixed(2))
  emit('update:height', +pxToMm(live.height, 'y').toFixed(2))
  emit('update:positionX', +pxToMm(live.x, 'x').toFixed(2))
  emit('update:positionY', +pxToMm(live.y, 'y').toFixed(2))
}

const initTouchResize = (handle: typeof resizeState.handle, e: TouchEvent) => {
  if (e.touches.length !== 1) return
  const t = e.touches[0]
  resizeState.active = true
  resizeState.handle = handle
  resizeState.startX = t.clientX
  resizeState.startY = t.clientY
  resizeState.startWidth = widthPx.value
  resizeState.startHeight = heightPx.value
  resizeState.startPosX = pos.x
  resizeState.startPosY = pos.y
  contextMenu.visible = false

  live.width = widthPx.value
  live.height = heightPx.value
  live.x = pos.x
  live.y = pos.y

  window.addEventListener('touchmove', resizeTouchMove, { passive: false })
  window.addEventListener('touchend', resizeTouchEnd)
  window.addEventListener('touchcancel', resizeTouchEnd)
}

const resizeTouchMove = (e: TouchEvent) => {
  const t = e.touches[0]
  // na touch voľný resize (pomer rieši multitouch nižšie)
  updateResize(t.clientX, t.clientY, false)
}

const resizeTouchEnd = () => {
  resizeState.active = false
  window.removeEventListener('touchmove', resizeTouchMove)
  window.removeEventListener('touchend', resizeTouchEnd)
  window.removeEventListener('touchcancel', resizeTouchEnd)

  emit('update:width', +pxToMm(live.width, 'x').toFixed(2))
  emit('update:height', +pxToMm(live.height, 'y').toFixed(2))
  emit('update:positionX', +pxToMm(live.x, 'x').toFixed(2))
  emit('update:positionY', +pxToMm(live.y, 'y').toFixed(2))
}

// 🔧 Voľné resizovanie po osi X/Y (už NIE „cover“ logika)
//    - myš: drž SHIFT pre uzamknutie pomeru strán
//    - touch: voľne; pre udržanie pomeru použi multitouch (nižšie)
function updateResize(clientX: number, clientY: number, lockRatio = false) {
  if (!resizeState.active) return

  const dx = clientX - resizeState.startX
  const dy = clientY - resizeState.startY

  const baseW = resizeState.startWidth
  const baseH = resizeState.startHeight
  const ratio = originalAspectRatio.value || 1

  // nezávislé škálovanie podľa smeru ťahu
  let newW = isLeftHandle(resizeState.handle) ? baseW - dx : baseW + dx
  let newH = isTopHandle(resizeState.handle) ? baseH - dy : baseH + dy

  newW = Math.max(20, newW)
  newH = Math.max(20, newH)

  // drž SHIFT → uzamkni pomer strán (vyber dominantu podľa väčšej zmeny)
  if (lockRatio) {
    if (Math.abs(dx) >= Math.abs(dy)) {
      newH = newW / ratio
    } else {
      newW = newH * ratio
    }
  }

  // udrži protiľahlý roh ako kotvu
  let newX = resizeState.startPosX
  let newY = resizeState.startPosY
  if (isLeftHandle(resizeState.handle)) {
    const right = resizeState.startPosX + baseW
    newX = right - newW
  }
  if (isTopHandle(resizeState.handle)) {
    const bottom = resizeState.startPosY + baseH
    newY = bottom - newH
  }

  const clamped = clampRect(newX, newY, newW, newH)
  live.x = clamped.x
  live.y = clamped.y
  live.width = clamped.w
  live.height = clamped.h

  // okamžité prekreslenie
  pos.x = live.x
  pos.y = live.y
}

// --- Multitouch zoom (udržanie pomeru strán) ---
let multitouchInitialDistance = 0
let multitouchInitialWidth = 0
const getTouchDistance = (touches: TouchList): number => {
  const dx = touches[1].clientX - touches[0].clientX
  const dy = touches[1].clientY - touches[0].clientY
  return Math.sqrt(dx * dx + dy * dy)
}
const onMultitouchStart = (e: TouchEvent) => {
  if (e.touches.length !== 2) return
  multitouchInitialDistance = getTouchDistance(e.touches)
  multitouchInitialWidth = widthPx.value
  window.addEventListener('touchmove', onMultitouchMove, { passive: false })
  window.addEventListener('touchend', onMultitouchEnd)
  window.addEventListener('touchcancel', onMultitouchEnd)
}
const onMultitouchMove = (e: TouchEvent) => {
  if (e.touches.length !== 2) return
  const newDistance = getTouchDistance(e.touches)
  const scale = newDistance / multitouchInitialDistance
  const newWidth = Math.max(20, multitouchInitialWidth * scale)
  const newHeight = newWidth / (originalAspectRatio.value || 1)
  live.width = newWidth
  live.height = newHeight
  emit('update:width', +pxToMm(newWidth, 'x').toFixed(2))
  emit('update:height', +pxToMm(newHeight, 'y').toFixed(2))
}
const onMultitouchEnd = () => {
  window.removeEventListener('touchmove', onMultitouchMove)
  window.removeEventListener('touchend', onMultitouchEnd)
  window.removeEventListener('touchcancel', onMultitouchEnd)
}

// ---------- REACT NA ZMENU ŠKÁLY / PARENT ROZMEROV ----------

// 1) Jednotná hydratácia z props (mm -> px)
function recomputeFromProps() {
  // od mm vždy nanovo
  pos.x = mmToPx(props.positionX, 'x')
  pos.y = mmToPx(props.positionY, 'y')
  live.x = pos.x
  live.y = pos.y

  const w = mmToPx(props.width, 'x')
  const h = mmToPx(props.height, 'y')
  live.width = w
  live.height = h

  // aktualizuj „originál“ (pre reset)
  originalDimensions.width = w
  originalDimensions.height = h
}

// 2) sleduj props a priebežne aktualizuj (ak neprebieha interakcia)
watch(
  [() => props.positionX, () => props.positionY, () => props.width, () => props.height],
  () => {
    if (isDragging.value || resizeState.active) return
    recomputeFromProps()
  },
  { immediate: true }
)

// 3) sleduj zmenu veľkosti rodičovského wrapperu (zmena škály)
let ro: ResizeObserver | null = null
onMounted(async () => {
  // až je element v DOMe, nájdi rodiča (canvas wrapper)
  await nextTick()
  const parent = container.value?.parentElement
  if (parent) {
    ro = new ResizeObserver(() => {
      // pri každom rescale znovu prepočítaj mm->px
      if (!isDragging.value && !resizeState.active) {
        recomputeFromProps()
      }
    })
    ro.observe(parent)
  }

  container.value?.addEventListener('pointerdown', onContainerPointerDown)
  container.value?.addEventListener('touchstart', onMultitouchStart, { passive: false })
})

onBeforeUnmount(() => {
  if (ro) {
    try { ro.disconnect() } catch (_) {}
    ro = null
  }
  container.value?.removeEventListener('pointerdown', onContainerPointerDown)
  container.value?.removeEventListener('touchstart', onMultitouchStart)
  window.removeEventListener('pointermove', onContainerPointerMove)
  window.removeEventListener('pointerup', onContainerPointerUp)
  window.removeEventListener('pointercancel', onContainerPointerUp)
  window.removeEventListener('pointermove', resizePointerMove)
  window.removeEventListener('pointerup', resizePointerUp)
  window.removeEventListener('pointercancel', resizePointerUp)
  window.removeEventListener('touchmove', onTouchMove)
  window.removeEventListener('touchend', onTouchEnd)
  window.removeEventListener('touchcancel', onTouchEnd)
  window.removeEventListener('touchmove', resizeTouchMove)
  window.removeEventListener('touchend', resizeTouchEnd)
  window.removeEventListener('touchcancel', resizeTouchEnd)
  window.removeEventListener('touchmove', onMultitouchMove)
  window.removeEventListener('touchend', onMultitouchEnd)
  window.removeEventListener('touchcancel', onMultitouchEnd)
})

const containerStyle = computed<Partial<CSSProperties>>(() => ({
  position: 'absolute',
  left: `${resizeState.active ? live.x : pos.x}px`,
  top: `${resizeState.active ? live.y : pos.y}px`,
  width: `${resizeState.active ? live.width : widthPx.value}px`,
  height: `${resizeState.active ? live.height : heightPx.value}px`,
  boxSizing: 'border-box',
}))

watch(
  () => useDesignerStore().scaleTick,
  () => {
    if (!container.value) return;
    if (!isDragging.value && !resizeState.active) {
      recomputeFromProps();
    }
  }
)

defineExpose({ container })
</script>

<style scoped>
.draggable-container {
  cursor: pointer;
  border: 1px dashed blue;
  /* ⚠️ neorezávaj obsah; chceme vidieť obrázok tak, ako sa vojde do kontajnera */
  overflow: visible;
  touch-action: none;
}
.label-image {
  width: 100%;
  height: 100%;
  pointer-events: none;
}
.resize-handle {
  width: 12px;
  height: 12px;
  background: white;
  border: 1px solid black;
  position: absolute;
  z-index: 10;
  touch-action: none;
}
@media (pointer: coarse) {
  .resize-handle {
    width: 18px;
    height: 18px;
  }
}
.resize-handle.top-left { top: -6px; left: -6px; cursor: nwse-resize; }
.resize-handle.top-right { top: -6px; right: -6px; cursor: nesw-resize; }
.resize-handle.bottom-left { bottom: -6px; left: -6px; cursor: nesw-resize; }
.resize-handle.bottom-right { bottom: -6px; right: -6px; cursor: nwse-resize; }

.context-menu {
  position: fixed;
  background: white;
  border: 1px solid #ccc;
  z-index: 100;
  padding: 6px;
  border-radius: 6px;
}
.context-menu button { background: transparent; border: none; padding: 4px 8px; cursor: pointer; }
.context-menu button:hover { background: #eee; }
</style>
